import React, { useMemo } from 'react'

import clsx from 'clsx'
import { UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { Icon, TableCell, Tooltip, Txt } from 'src/components/atoms'
import { CompassRoleDropdown, DateTxt } from 'src/components/molecules'
import { LineLimitTxtWithTooltip } from 'src/components/molecules/texts/LineLimitTxtWithTooltip'

import { useGetUsers } from 'src/hooks'
import { ProfileEntity, UpsertProjectFormEntity } from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type Props = {
  profile?: ProfileEntity
  userId: number
  compassRoles: CompassRoleEntity[]
  selectedValue: number | undefined
  isRemoveDisabled?: boolean
  errorMessage?: string
  alreadyAssignedInternalUserIds?: number[]
  assignedAt?: Date
  watch: UseFormWatch<UpsertProjectFormEntity>
  setValue: UseFormSetValue<UpsertProjectFormEntity>
  trigger: UseFormTrigger<UpsertProjectFormEntity>
  className?: string
}

export function InternalAssignUserEditTableRow({
  profile,
  userId,
  compassRoles,
  selectedValue,
  isRemoveDisabled = false,
  errorMessage,
  alreadyAssignedInternalUserIds,
  assignedAt,
  watch,
  setValue,
  trigger,
  className,
}: Props) {
  const { users } = useGetUsers()

  const targetUser = useMemo(() => users.find(u => u.id === userId), [users, userId])

  const isShowAssignBatch = useMemo(() => {
    return !alreadyAssignedInternalUserIds?.some(id => id === userId)
  }, [alreadyAssignedInternalUserIds, userId])

  const onClickRemoveUser = async (event: React.MouseEvent) => {
    event.preventDefault()
    if (isRemoveDisabled) {
      return
    }
    const updatedUsers = watch('projectUsers').filter(user => user.id !== userId)
    setValue('projectUsers', updatedUsers)
    await trigger('projectUsers')
  }

  const onSelectCompassRole = async (compassRoleId: number | undefined) => {
    const targetCompassRole = compassRoles.find(role => role.id === compassRoleId)
    if (!targetCompassRole) {
      // あり得ないはずだが念のため
      throw new Error('compassRole not found')
    }
    const updatedUsers = watch('projectUsers').map(user => {
      if (user.id === userId) {
        return { ...user, compassRoleId, compassRolePriority: targetCompassRole.priority }
      }
      return user
    })
    setValue('projectUsers', updatedUsers)

    // 対象となるユーザーのみバリデーションの再検証を行う
    const targetIndex = watch('projectUsers').findIndex(user => user.id === userId)
    await trigger(`projectUsers.${targetIndex}`)
  }

  return (
    <div className={className}>
      <div className="tw-flex tw-gap-s0 tw-px-s0 tw-text-standardText-secondary tw-py-s-2">
        <TableCell className="tw-w-full tw-shrink tw-relative tw-h-[3.29rem]">
          <LineLimitTxtWithTooltip
            className={clsx(
              'tw-break-all',
              targetUser?.id === profile?.id ? 'tw-text-pattern6-3' : 'tw-text-standardText-primary'
            )}
            text={targetUser?.name ?? '-'}
            lineLimit={2}
          />
          {isShowAssignBatch && (
            <div className="tw-absolute tw-top-s-4 -tw-left-s-3 tw-bg-errorRed-default tw-w-[10px] tw-aspect-square tw-rounded-full" />
          )}
        </TableCell>
        <TableCell width={13}>
          <LineLimitTxtWithTooltip
            className="tw-break-all"
            text={targetUser?.email ?? '-'}
            lineLimit={2}
          />
        </TableCell>
        <TableCell width={13}>
          <LineLimitTxtWithTooltip
            className="tw-break-all"
            text={
              targetUser?.keywords && targetUser.keywords.length > 0
                ? targetUser.keywords.join(',')
                : '-'
            }
            lineLimit={2}
          />
        </TableCell>
        <TableCell width={13}>
          <LineLimitTxtWithTooltip
            className="tw-break-all"
            text={
              targetUser?.customRoles && targetUser.customRoles.length > 0
                ? targetUser.customRoles.map(role => role.name).join(',')
                : '-'
            }
            lineLimit={2}
          />
        </TableCell>
        <TableCell width={11}>
          {assignedAt ? <DateTxt date={assignedAt} /> : <Txt>-</Txt>}
        </TableCell>
        <TableCell width={25}>
          <CompassRoleDropdown
            size="m"
            compassRoles={compassRoles}
            value={selectedValue}
            selectOption={onSelectCompassRole}
            errorMessage={errorMessage}
            className="tw-mr-s-3 tw-w-full"
          />
          <button type="button" onClick={onClickRemoveUser}>
            <Tooltip
              placement="top"
              showIcon={false}
              text="プロジェクトから外す"
              disabled={isRemoveDisabled}
            >
              <Icon
                size="--r1"
                path="trash/soft/line"
                className={
                  isRemoveDisabled
                    ? 'tw-text-standardText-disable'
                    : 'tw-text-standardText-secondary hover:tw-text-standardText-primary'
                }
              />
            </Tooltip>
          </button>
        </TableCell>
      </div>
    </div>
  )
}
